import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate } from "react-router-dom";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchBillingLabsDetailed,
  saveLabBillingPricing,
  type LabBillingDetailedRow,
} from "../../store/superAdmin/labBillingPricingSlice";
import { BILLING_CATEGORIES } from "../../utils/jobCard";
import { canAccessLabPricing } from "../../utils/employeeRoles";
import { useToast } from "../../components/utilities/Toast";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminSelect,
  AdminSoftInput,
  AdminPrimaryButton,
  AdminTableCard,
  AdminTableState,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

type PricingFilter = "all" | "set" | "unset";

type DraftRow = {
  lab_name: string;
  currency: string;
  prices: Record<string, string>;
};

const emptyDraftPrices = (): Record<string, string> => {
  const prices: Record<string, string> = {};
  for (const category of BILLING_CATEGORIES) {
    prices[category] = "0";
  }
  return prices;
};

const toDraft = (row: LabBillingDetailedRow): DraftRow => {
  const prices = emptyDraftPrices();
  for (const category of BILLING_CATEGORIES) {
    const value = row.prices?.[category];
    prices[category] =
      value === undefined || value === null ? "0" : String(value);
  }
  return {
    lab_name: row.lab_name || "",
    currency: row.currency || "",
    prices,
  };
};

const shortCategoryLabel = (category: string) => {
  if (category === "Crown & Bridge – Implant / Smile Design") return "C&B Implant/Smile";
  if (category === "Crown & Bridge – With Model") return "C&B + Model";
  if (category === "Crown & Bridge – Grade II") return "C&B Grade II";
  if (category === "Crown & Bridge – Grade II With Model") return "C&B GII + Model";
  if (category === "Crown & Bridge") return "C&B";
  return category;
};

const LabBillingPricingDetails = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { detailedLabs, detailedLoading, error } = useAppSelector(
    (state) => state.labBillingPricing
  );

  const [labSearch, setLabSearch] = useState("");
  const [pricingFilter, setPricingFilter] = useState<PricingFilter>("all");
  const [drafts, setDrafts] = useState<Record<string, DraftRow>>({});
  const [savingToken, setSavingToken] = useState<string | null>(null);

  const allowed = canAccessLabPricing(user?.role, employee?.role);

  useEffect(() => {
    if (!allowed) return;
    dispatch(fetchBillingLabsDetailed());
  }, [dispatch, allowed]);

  useEffect(() => {
    const next: Record<string, DraftRow> = {};
    for (const row of detailedLabs) {
      next[row.lab_token] = toDraft(row);
    }
    setDrafts(next);
  }, [detailedLabs]);

  useEffect(() => {
    if (error) showToast(error, "error");
  }, [error, showToast]);

  const pricedCount = useMemo(
    () => detailedLabs.filter((lab) => lab.has_pricing).length,
    [detailedLabs]
  );
  const unpricedCount = useMemo(
    () => detailedLabs.filter((lab) => !lab.has_pricing).length,
    [detailedLabs]
  );

  const visibleLabs = useMemo(() => {
    let list = detailedLabs;
    if (pricingFilter === "set") {
      list = list.filter((lab) => lab.has_pricing);
    } else if (pricingFilter === "unset") {
      list = list.filter((lab) => !lab.has_pricing);
    }

    const q = labSearch.trim().toLowerCase();
    if (!q) return list;

    return list.filter((lab) => {
      const haystack = [
        lab.lab_token,
        lab.lab_name,
        lab.user_name,
        lab.organisation,
        lab.currency,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [detailedLabs, labSearch, pricingFilter]);

  const updateDraft = useCallback(
    (token: string, patch: Partial<Omit<DraftRow, "prices">>) => {
      setDrafts((prev) => {
        const current =
          prev[token] ||
          ({
            lab_name: "",
            currency: "",
            prices: emptyDraftPrices(),
          } satisfies DraftRow);
        return {
          ...prev,
          [token]: {
            ...current,
            ...patch,
          },
        };
      });
    },
    []
  );

  const updatePrice = useCallback(
    (token: string, category: string, value: string) => {
      setDrafts((prev) => {
        const current = prev[token];
        if (!current) return prev;
        return {
          ...prev,
          [token]: {
            ...current,
            prices: { ...current.prices, [category]: value },
          },
        };
      });
    },
    []
  );

  const isDirty = useCallback(
    (row: LabBillingDetailedRow) => {
      const draft = drafts[row.lab_token];
      if (!draft) return false;
      if (draft.lab_name.trim() !== (row.lab_name || "").trim()) return true;
      if (draft.currency.trim() !== (row.currency || "").trim()) return true;
      for (const category of BILLING_CATEGORIES) {
        const draftVal = Number(draft.prices[category]);
        const savedVal = Number(row.prices?.[category] ?? 0);
        if (!Number.isFinite(draftVal) || draftVal !== savedVal) return true;
      }
      return false;
    },
    [drafts]
  );

  const handleSave = async (row: LabBillingDetailedRow) => {
    const draft = drafts[row.lab_token];
    if (!draft) return;

    if (!draft.lab_name.trim() || !draft.currency.trim()) {
      showToast("Lab name and currency are required", "error");
      return;
    }

    const numericPrices: Record<string, number> = {};
    for (const category of BILLING_CATEGORIES) {
      const parsed = Number(draft.prices[category]);
      if (!Number.isFinite(parsed) || parsed < 0) {
        showToast(`Invalid price for ${category}`, "error");
        return;
      }
      numericPrices[category] = Math.round(parsed * 100) / 100;
    }

    setSavingToken(row.lab_token);
    try {
      await dispatch(
        saveLabBillingPricing({
          labToken: row.lab_token,
          invoice_name: draft.lab_name.trim(),
          currency: draft.currency.trim(),
          prices: numericPrices,
        })
      ).unwrap();
      showToast(`Saved pricing for ${row.lab_token}`, "success");
    } catch (err: any) {
      showToast(String(err || "Failed to save pricing"), "error");
    } finally {
      setSavingToken(null);
    }
  };

  if (!allowed) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Lab Pricing Details</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Lab Pricing Details"
          subtitle="View and edit rates for every lab — token, org, currency, and category prices"
        >
          <Link
            to="/dashboard/admin/lab-pricing"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-white/15 text-white text-xs font-medium border border-white/30 hover:bg-white/25 transition"
          >
            <FiArrowLeft className="text-sm" />
            Lab Pricing
          </Link>
          <AdminSelect
            value={pricingFilter}
            onChange={(v) => setPricingFilter(v as PricingFilter)}
            className="w-full sm:w-[200px]"
          >
            <option value="all">All labs ({detailedLabs.length})</option>
            <option value="set">Pricing set ({pricedCount})</option>
            <option value="unset">Pricing not set ({unpricedCount})</option>
          </AdminSelect>
          <AdminSearchInput
            value={labSearch}
            onChange={setLabSearch}
            placeholder="Search token, lab, org…"
            className="w-full sm:w-[260px]"
          />
        </AdminPageHeader>

        <AdminTableCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1400px]">
              <thead>
                <tr className="border-b border-[#E8E8E4] bg-[#F8F8F6]">
                  <th className={`${adminTableHead} sticky left-0 z-10 bg-[#F8F8F6] min-w-[72px]`}>
                    Token
                  </th>
                  <th className={`${adminTableHead} min-w-[140px]`}>Lab Name</th>
                  <th className={`${adminTableHead} min-w-[140px]`}>Org Name</th>
                  <th className={`${adminTableHead} min-w-[88px]`}>Currency</th>
                  <th className={`${adminTableHead} min-w-[72px] text-center`}>
                    Status
                  </th>
                  {BILLING_CATEGORIES.map((category) => (
                    <th
                      key={category}
                      className={`${adminTableHead} min-w-[100px] text-center`}
                      title={category}
                    >
                      {shortCategoryLabel(category)}
                    </th>
                  ))}
                  <th className={`${adminTableHead} sticky right-0 z-10 bg-[#F8F8F6] min-w-[100px] text-center`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {detailedLoading && detailedLabs.length === 0 ? (
                  <AdminTableState
                    colSpan={6 + BILLING_CATEGORIES.length}
                    variant="loading"
                    message="Loading lab pricing…"
                  />
                ) : visibleLabs.length === 0 ? (
                  <AdminTableState
                    colSpan={6 + BILLING_CATEGORIES.length}
                    variant="empty"
                    message={
                      pricingFilter === "set"
                        ? "No labs with pricing set"
                        : pricingFilter === "unset"
                          ? "No labs without pricing"
                          : "No labs found"
                    }
                  />
                ) : (
                  visibleLabs.map((row) => {
                    const draft = drafts[row.lab_token] || toDraft(row);
                    const dirty = isDirty(row);
                    const saving = savingToken === row.lab_token;
                    return (
                      <tr
                        key={row.lab_token}
                        className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8]"
                      >
                        <td
                          className={`${adminTableCell} sticky left-0 z-10 bg-white font-semibold text-[#1A1A18]`}
                        >
                          {row.lab_token}
                        </td>
                        <td className={adminTableCell}>
                          <AdminSoftInput
                            value={draft.lab_name}
                            onChange={(e) =>
                              updateDraft(row.lab_token, {
                                lab_name: e.target.value,
                              })
                            }
                            placeholder="Billing lab name"
                            className="w-full min-w-[120px] py-1.5 px-2 text-xs"
                          />
                        </td>
                        <td className={`${adminTableCell} text-sm text-[#4A4A48]`}>
                          {row.organisation || "—"}
                        </td>
                        <td className={adminTableCell}>
                          <AdminSoftInput
                            value={draft.currency}
                            onChange={(e) =>
                              updateDraft(row.lab_token, {
                                currency: e.target.value,
                              })
                            }
                            placeholder="USD"
                            className="w-full min-w-[72px] py-1.5 px-2 text-xs"
                          />
                        </td>
                        <td className={`${adminTableCell} text-center`}>
                          <span
                            className={`inline-block text-[10px] font-semibold uppercase tracking-wide rounded-md px-1.5 py-0.5 ${
                              row.has_pricing
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}
                          >
                            {row.has_pricing ? "Set" : "Not set"}
                          </span>
                        </td>
                        {BILLING_CATEGORIES.map((category) => (
                          <td key={category} className={`${adminTableCell} text-center`}>
                            <AdminSoftInput
                              type="number"
                              min={0}
                              step="0.01"
                              value={draft.prices[category] ?? "0"}
                              onChange={(e) =>
                                updatePrice(
                                  row.lab_token,
                                  category,
                                  e.target.value
                                )
                              }
                              className="w-full max-w-[88px] mx-auto py-1.5 px-1.5 text-xs text-center"
                            />
                          </td>
                        ))}
                        <td className={`${adminTableCell} sticky right-0 z-10 bg-white text-center`}>
                          <AdminPrimaryButton
                            type="button"
                            onClick={() => handleSave(row)}
                            disabled={saving || !dirty}
                            className="px-3 py-1.5 text-[11px]"
                          >
                            <FiSave className="text-sm" />
                            {saving ? "Saving…" : "Save"}
                          </AdminPrimaryButton>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </AdminTableCard>
      </AdminPageShell>
    </>
  );
};

export default LabBillingPricingDetails;
